import { useStore as useZustand } from 'zustand';
import { useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth/store';
import { GRAY } from '../people';
import { createStore, type StoreState } from './createStore';
import type { Row, TableName } from './model';

// Единственный экземпляр на приложение. Стартует из App, когда профиль прочитан.
export const data = createStore(supabase);

let stopRoleSync: (() => void) | null = null;

export function startData() {
  // selfId — чтобы не подсвечивать свои правки как чужие; имя и цвет — для присутствия.
  const { profile, session } = useAuth.getState();
  if (profile) {
    // имя и картинка — из аккаунта Google, если вход через него; иначе кусок email и буквы
    const meta = (session?.user.user_metadata ?? {}) as { full_name?: string; avatar_url?: string; picture?: string };
    const name = meta.full_name || (profile.email.split('@')[0] ?? profile.email);
    const self = { id: profile.id, name, color: GRAY, avatar: meta.avatar_url ?? meta.picture ?? null, role: profile.role };
    data.setSelf(self);
    // роль сменил админ — своя строка profile приходит журналом: кнопки и проверка роли обновляются без перезагрузки
    stopRoleSync = data.store.subscribe((s) => {
      const row = s.tables.profile.get(profile.id);
      if (row && row.role !== useAuth.getState().profile?.role) { useAuth.setState({ profile: row }); data.setSelf({ ...self, role: row.role }); }
    });
  }
  return data.start();
}

export function stopData() { stopRoleSync?.(); stopRoleSync = null; data.stop(); }

/** Личная настройка; null снимает её: экран возвращается к поведению по умолчанию (fallback считается заново при каждом рендере). */
export function useSetting<T>(key: string, fallback: T): [T, (v: T | null) => void] {
  useData((s) => s.settings);
  useData((s) => s.tables.shared_setting);
  const value = data.setting<T>(key, fallback);
  // Ссылка меняется только вместе со значением: fallback-литерал (`[]`, defaultRange()) — новый объект на каждый
  // рендер, и без этого сетка, дорожки и подсветка пересчитывали свои useMemo вхолостую. Настройки — маленький JSON.
  const last = useRef<{ json: string; value: T } | null>(null);
  const json = JSON.stringify(value);
  if (!last.current || last.current.json !== json) last.current = { json, value };
  const set = useCallback((v: T | null) => { void data.setSetting(key, v); }, [key]);
  return [last.current.value, set];
}

export function useData<T>(selector: (s: StoreState) => T): T {
  return useZustand(data.store, selector);
}

// Таблица с наложенными ожидающими правками; пересчитывается при изменении таблицы или ожидающих.
export function useTable<T extends TableName>(name: T): Map<string, Row<T>> {
  const table = useData((s) => s.tables[name]);
  const pending = useData((s) => s.pending);
  return useMemo(() => data.view(name) as Map<string, Row<T>>, [name, table, pending]);
}
